import { Button, Form } from 'components';
import { MediaUpload } from 'components/Form/inputs/MediaUpload/MediaUpload';
import validationSchemas from 'containers/forms/schemas';
import LocationImagesPreview from 'pages/Location/components/preview/LocationImagesPreview';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { media } from 'utils/rwd';
import { useUpdateLocation } from 'api/locations/hooks';
import { useUploadMedia } from 'hooks/useUploadMedia';

const LocationImagesForm = ({
  type = 'update',
  initialValues,
  onNext,
  onPrevious,
  defaultCoverCropMode = 'square',
}) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { mutate: updateLocation, isPending: isUpdatingLocation } = useUpdateLocation();
  const { mutateAsync: uploadMediaAsync, isPending: isUploadingMedia } = useUploadMedia();
  const isSubmitting = isUploadingMedia || isUpdatingLocation;

  const formInitialValues = useMemo(
    () => ({
      uid: initialValues?.uid || undefined,
      logo: initialValues.logo ? [initialValues.logo] : [],
      coverImage: initialValues.coverImage ? [initialValues.coverImage] : [],
      logoId: initialValues.logoId ? [initialValues.logoId] : [],
      coverImageId: initialValues.coverImageId ? [initialValues.coverImageId] : [],
    }),
    [initialValues],
  );
  const validationSchema = useMemo(() => validationSchemas.location_images, []);

  const handleSubmit = useCallback(
    async ({ uid, logoId, coverImageId }) => {
      let publicLogoId = '';
      let publicCoverImageId = '';

      if (Array.isArray(logoId) && logoId.length > 0) {
        const { public_id } = await uploadMediaAsync({ media: logoId });
        publicLogoId = public_id;
      }

      if (Array.isArray(coverImageId) && coverImageId.length > 0) {
        const { public_id } = await uploadMediaAsync({ media: coverImageId });
        publicCoverImageId = public_id;
      }

      if (type === 'update') {
        updateLocation(
          {
            uid,
            content: {
              logo_id: publicLogoId,
              cover_image_id: publicCoverImageId,
              onboarding_completed: true,
            },
          },
          {
            onSuccess: () => {
              if (onNext) onNext();
            },
          },
        );
      } else if (onNext) {
        onNext();
      }
    },
    [updateLocation, uploadMediaAsync, type, onNext],
  );

  return (
    <Form
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      renderDefaultButtons={false}
    >
      {({ values }) => (
        <Content>
          <ImagesPreview
            logo={
              values.logoId[0] instanceof File
                ? URL.createObjectURL(values.logoId[0])
                : values.logo[0]
            }
            coverImage={
              values.coverImageId[0] instanceof File
                ? URL.createObjectURL(values.coverImageId[0])
                : values.coverImage[0]
            }
          />

          <UploadSection>
            <MediaUploadContainer>
              <Form.GroupHorizontal label={t('onboarding.logoLabel')}>
                <MediaUpload
                  accept={{
                    'image/png': ['.png'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                    'image/webp': ['.webp'],
                  }}
                  name="logoId"
                  imageField="logo"
                  maxFiles={1}
                  withThumbnails={false}
                  withCrop
                />
              </Form.GroupHorizontal>
            </MediaUploadContainer>

            <MediaUploadContainer>
              <Form.GroupHorizontal label={t('onboarding.coverImageLabel')}>
                <MediaUpload
                  accept={{
                    'image/*': [],
                    'image/webp': ['.webp'],
                  }}
                  name="coverImageId"
                  imageField="coverImage"
                  maxFiles={1}
                  withThumbnails={false}
                  withCrop
                  aspectRatio={16 / 9}
                  defaultCropMode={defaultCoverCropMode}
                />
              </Form.GroupHorizontal>
            </MediaUploadContainer>
          </UploadSection>

          <ButtonsSection>
            {onPrevious && (
              <Button variant="secondary" block onClick={onPrevious}>
                {t('onboarding.previousStep')}
              </Button>
            )}
            <Form.Submit
              className="text-uppercase mt-0"
              size="lg"
              variant="primary"
              block
              isLoading={isSubmitting}
              ignoreClickedState
            >
              {t('onboarding.finish')}
            </Form.Submit>
          </ButtonsSection>
        </Content>
      )}
    </Form>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const ImagesPreview = styled(LocationImagesPreview)`
  max-width: 600px;
`;

const UploadSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;

  ${media.md`
    flex-direction: row;
    gap: 32px;
  `}
`;

const MediaUploadContainer = styled.div`
  flex: 1;
`;

const ButtonsSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

LocationImagesForm.propTypes = {
  type: PropTypes.oneOf(['update']),
  initialValues: PropTypes.shape({
    uid: PropTypes.string,
    logo: PropTypes.string,
    coverImage: PropTypes.string,
    logoId: PropTypes.string,
    coverImageId: PropTypes.string,
  }),
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  defaultCoverCropMode: PropTypes.oneOf(['square', 'rectangle']),
};

export default LocationImagesForm;
